/**
 * Jet A ballpark (US gal) per ICAO — operator-sim planning, not a posted FBO
 * invoice and not Avgas.
 *
 * No reliable free per-airport feed is legal to scrape and cache. This module
 * ships a curated regional table (CAD + USD pairs) plus Settings overrides.
 * Refresh is slow (days–months / desk ship). Fail soft to the company default.
 *
 * See docs/JET_A.md.
 */
import { icaoKey } from "./curfew";
import type { CurrencyCode } from "./catalogs";

/** Snapshot month for the curated table. Not a live market print. */
export const JET_A_AS_OF = "2026-09";

export const JET_A_REFRESH_NOTE =
  "Curated regional table. Refresh with a desk ship (days–months). No live FBO scrape.";

export const JET_A_DEFAULT_CAD = 8.5;
export const JET_A_DEFAULT_USD = 6.2;

export const JET_A_OVERRIDE_NOTE = "Settings override";

export type JetASource = "override" | "catalog" | "icao" | "region" | "country" | "default";

export type JetARegionId =
  | "CA-NS"
  | "CA-NB"
  | "CA-PE"
  | "CA-NL"
  | "CA-QC"
  | "CA-ON"
  | "CA-BC"
  | "CA"
  | "US-NY"
  | "US-NE"
  | "US-FL"
  | "US-SE"
  | "US-MW"
  | "US-W"
  | "US"
  | "CARIB"
  | "MX"
  | "EU"
  | "OTHER";

export type JetARegionRow = {
  id: JetARegionId;
  label: string;
  cadPerUsGal: number;
  usdPerUsGal: number;
};

export type JetAQuote = {
  icao: string;
  pricePerUsGal: number;
  currency: CurrencyCode;
  source: JetASource;
  regionId: JetARegionId;
  regionLabel: string;
  asOf: string;
  note: string;
};

export type JetAOverride = {
  icao: string;
  pricePerUsGal: number;
};

export type JetACatalogFuel = {
  icao: string;
  fuel: string;
  pricePerGal: number;
};

export type LookupJetAInput = {
  icao?: string | null;
  homeCurrency?: CurrencyCode;
  catalogs?: { currency?: CurrencyCode; fuel?: readonly JetACatalogFuel[] };
  overrides?: readonly JetAOverride[] | null;
  fallbackPerGal?: number;
};

export const JET_A_SOURCE_LABEL: Record<JetASource, string> = {
  override: "Settings override",
  catalog: "Catalog",
  icao: "Airport table",
  region: "Regional default",
  country: "Country default",
  default: "Company default",
};

/** Paired CAD / USD planning figures — not a live FX conversion. */
export const JET_A_REGIONS: readonly JetARegionRow[] = [
  { id: "CA-NS", label: "Nova Scotia", cadPerUsGal: 8.5, usdPerUsGal: 6.2 },
  { id: "CA-NB", label: "New Brunswick", cadPerUsGal: 8.45, usdPerUsGal: 6.15 },
  { id: "CA-PE", label: "Prince Edward Island", cadPerUsGal: 8.55, usdPerUsGal: 6.25 },
  { id: "CA-NL", label: "Newfoundland and Labrador", cadPerUsGal: 8.7, usdPerUsGal: 6.35 },
  { id: "CA-QC", label: "Quebec", cadPerUsGal: 8.35, usdPerUsGal: 6.1 },
  { id: "CA-ON", label: "Ontario", cadPerUsGal: 8.2, usdPerUsGal: 6.0 },
  { id: "CA-BC", label: "British Columbia", cadPerUsGal: 8.15, usdPerUsGal: 5.95 },
  { id: "CA", label: "Canada", cadPerUsGal: 8.5, usdPerUsGal: 6.2 },
  { id: "US-NY", label: "New York metro", cadPerUsGal: 10.2, usdPerUsGal: 7.45 },
  { id: "US-NE", label: "New England / Mid-Atlantic", cadPerUsGal: 8.9, usdPerUsGal: 6.5 },
  { id: "US-FL", label: "Florida", cadPerUsGal: 7.9, usdPerUsGal: 5.75 },
  { id: "US-SE", label: "US Southeast", cadPerUsGal: 8.1, usdPerUsGal: 5.9 },
  { id: "US-MW", label: "US Midwest", cadPerUsGal: 8.0, usdPerUsGal: 5.85 },
  { id: "US-W", label: "US West", cadPerUsGal: 8.25, usdPerUsGal: 6.0 },
  { id: "US", label: "United States", cadPerUsGal: 8.5, usdPerUsGal: 6.2 },
  { id: "CARIB", label: "Caribbean / Bermuda", cadPerUsGal: 10.8, usdPerUsGal: 7.9 },
  { id: "MX", label: "Mexico", cadPerUsGal: 9.4, usdPerUsGal: 6.85 },
  { id: "EU", label: "Europe", cadPerUsGal: 10.5, usdPerUsGal: 7.65 },
  { id: "OTHER", label: "Other / unknown", cadPerUsGal: 8.5, usdPerUsGal: 6.2 },
];

const REGION_BY_ID: Record<JetARegionId, JetARegionRow> = Object.fromEntries(
  JET_A_REGIONS.map((row) => [row.id, row]),
) as Record<JetARegionId, JetARegionRow>;

/** Known Celtic / starter / dest-pack fields. Unknown ICAOs use prefix fallback. */
const ICAO_REGION: Record<string, JetARegionId> = {
  CYPD: "CA-NS",
  CYHZ: "CA-NS",
  CYQY: "CA-NS",
  CYYT: "CA-NL",
  CYQX: "CA-NL",
  CYQM: "CA-NB",
  CYYG: "CA-PE",
  CYUL: "CA-QC",
  CYHU: "CA-QC",
  CYOW: "CA-ON",
  CYYZ: "CA-ON",
  CYKZ: "CA-ON",
  CYTZ: "CA-ON",
  CYHM: "CA-ON",
  CYKF: "CA-ON",
  CYOO: "CA-ON",
  CYVR: "CA-BC",
  KTEB: "US-NY",
  KHPN: "US-NY",
  KJFK: "US-NY",
  KEWR: "US-NY",
  KMMU: "US-NY",
  KFRG: "US-NY",
  KSWF: "US-NY",
  KBOS: "US-NE",
  KBED: "US-NE",
  KBGR: "US-NE",
  KACK: "US-NE",
  KBDL: "US-NE",
  KPVD: "US-NE",
  KBTV: "US-NE",
  KORH: "US-NE",
  KMHT: "US-NE",
  KDCA: "US-NE",
  KIAD: "US-NE",
  KHEF: "US-NE",
  KBWI: "US-NE",
  KPHL: "US-NE",
  KMIA: "US-FL",
  KPBI: "US-FL",
  KFLL: "US-FL",
  KFXE: "US-FL",
  KOPF: "US-FL",
  KTPA: "US-FL",
  KRSW: "US-FL",
  KAPF: "US-FL",
  KMCO: "US-FL",
  KJAX: "US-FL",
  KBCT: "US-FL",
  KCLT: "US-SE",
  KCHS: "US-SE",
  KMYR: "US-SE",
  KILM: "US-SE",
  KCHA: "US-SE",
  KICT: "US-MW",
  KDAY: "US-MW",
  KCPT: "US-MW",
  KGJT: "US-W",
  KAPA: "US-W",
  KSDL: "US-W",
  TJSJ: "CARIB",
  MYNN: "CARIB",
  TXKF: "CARIB",
  TBPB: "CARIB",
  TNCM: "CARIB",
  MDPC: "CARIB",
  MKJS: "CARIB",
  MWCR: "CARIB",
  TNCA: "CARIB",
  TIST: "CARIB",
  MMUN: "MX",
  MMPR: "MX",
  MMSD: "MX",
  MMTO: "MX",
  MMMX: "MX",
  MMCZ: "MX",
  MMMY: "MX",
};

/** Optional ICAO-specific refinements when Catalogs has no row and Settings has no override. */
const ICAO_QUOTES: Record<string, { cadPerUsGal: number; usdPerUsGal: number; note: string }> = {
  KTEB: { cadPerUsGal: 11.0, usdPerUsGal: 8.05, note: "Teterboro exec FBO premium band" },
  KHPN: { cadPerUsGal: 10.6, usdPerUsGal: 7.75, note: "Westchester exec FBO premium band" },
  KMIA: { cadPerUsGal: 8.2, usdPerUsGal: 6.0, note: "Miami international band" },
  MYNN: { cadPerUsGal: 11.2, usdPerUsGal: 8.2, note: "Nassau island premium band" },
};

const COUNTRY_OF_REGION: Record<JetARegionId, JetARegionId> = {
  "CA-NS": "CA",
  "CA-NB": "CA",
  "CA-PE": "CA",
  "CA-NL": "CA",
  "CA-QC": "CA",
  "CA-ON": "CA",
  "CA-BC": "CA",
  CA: "CA",
  "US-NY": "US",
  "US-NE": "US",
  "US-FL": "US",
  "US-SE": "US",
  "US-MW": "US",
  "US-W": "US",
  US: "US",
  CARIB: "CARIB",
  MX: "MX",
  EU: "EU",
  OTHER: "OTHER",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function roundGal(value: number): number {
  return Math.round(value * 100) / 100;
}

function homeCurrencyOf(value: unknown): CurrencyCode {
  return value === "USD" || value === "EUR" || value === "GBP" || value === "CAD" ? value : "CAD";
}

function regionRow(id: JetARegionId): JetARegionRow {
  return REGION_BY_ID[id] ?? REGION_BY_ID.OTHER;
}

function amountFromPair(
  row: { cadPerUsGal: number; usdPerUsGal: number },
  home: CurrencyCode,
  fallback: number,
): number {
  if (home === "USD") return roundGal(row.usdPerUsGal);
  if (home === "CAD") return roundGal(row.cadPerUsGal);
  // EUR / GBP: table is CAD-denominated planning (same relabel catalogs use — not FX).
  return Number.isFinite(row.cadPerUsGal) ? roundGal(row.cadPerUsGal) : roundGal(fallback);
}

function countryFromPrefix(code: string): JetARegionId {
  if (code.startsWith("CY") || code.startsWith("CZ") || code.startsWith("CE")) return "CA";
  if (code.startsWith("K") && code.length === 4) return "US";
  if (code.startsWith("MM")) return "MX";
  if (
    code.startsWith("T") ||
    code.startsWith("MY") ||
    code.startsWith("MB") ||
    code.startsWith("MD") ||
    code.startsWith("MK") ||
    code.startsWith("MW") ||
    code.startsWith("TN") ||
    code.startsWith("TJ") ||
    code.startsWith("TF") ||
    code.startsWith("TV")
  ) {
    return "CARIB";
  }
  if (code.startsWith("E") || code.startsWith("L") || code.startsWith("BI")) return "EU";
  return "OTHER";
}

/** Province / state when known; else country / OTHER. Never throws. */
export function jetARegionIdForIcao(icao?: string | null): JetARegionId {
  const code = icaoKey(icao);
  if (!code) return "OTHER";
  const exact = ICAO_REGION[code];
  if (exact) return exact;
  return countryFromPrefix(code);
}

export function jetACountryIdForIcao(icao?: string | null): JetARegionId {
  return COUNTRY_OF_REGION[jetARegionIdForIcao(icao)] ?? "OTHER";
}

export function hydrateJetAOverrides(raw?: unknown): JetAOverride[] {
  if (!Array.isArray(raw)) return [];
  const out: JetAOverride[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!isRecord(row)) continue;
    const icao = icaoKey(typeof row.icao === "string" ? row.icao : "");
    const price = typeof row.pricePerUsGal === "number" ? row.pricePerUsGal : Number(row.pricePerUsGal);
    if (!icao || icao.length < 3 || icao.length > 4) continue;
    if (!Number.isFinite(price) || price <= 0) continue;
    if (seen.has(icao)) continue;
    seen.add(icao);
    out.push({ icao, pricePerUsGal: roundGal(price) });
  }
  return out.sort((a, b) => a.icao.localeCompare(b.icao));
}

export function upsertJetAOverride(list: readonly JetAOverride[], icao: string, pricePerUsGal: number): JetAOverride[] {
  const code = icaoKey(icao);
  const price = Number(pricePerUsGal);
  if (!code || !Number.isFinite(price) || price <= 0) return hydrateJetAOverrides(list);
  const rest = list.filter((row) => row.icao !== code);
  return hydrateJetAOverrides([...rest, { icao: code, pricePerUsGal: price }]);
}

export function removeJetAOverride(list: readonly JetAOverride[], icao: string): JetAOverride[] {
  const code = icaoKey(icao);
  return hydrateJetAOverrides(list.filter((row) => row.icao !== code));
}

export function jetAOverrideFor(list: readonly JetAOverride[] | null | undefined, icao: string): JetAOverride | undefined {
  const code = icaoKey(icao);
  if (!code) return undefined;
  return (list ?? []).find((row) => icaoKey(row.icao) === code);
}

function catalogJetA(catalogs: LookupJetAInput["catalogs"], icao: string): number | undefined {
  const fuel = catalogs?.fuel;
  if (!fuel?.length) return undefined;
  const hit = fuel.find((row) => row.icao === icao && row.fuel === "jeta");
  if (!hit || !Number.isFinite(hit.pricePerGal) || hit.pricePerGal <= 0) return undefined;
  return roundGal(hit.pricePerGal);
}

function catalogDefaultJetA(catalogs: LookupJetAInput["catalogs"]): number | undefined {
  return catalogJetA(catalogs, "*");
}

function quote(
  icao: string,
  price: number,
  currency: CurrencyCode,
  source: JetASource,
  regionId: JetARegionId,
  note: string,
): JetAQuote {
  const region = regionRow(regionId);
  return {
    icao,
    pricePerUsGal: roundGal(price),
    currency,
    source,
    regionId,
    regionLabel: region.label,
    asOf: JET_A_AS_OF,
    note,
  };
}

/**
 * Table amount for an ICAO in home CAD or USD. EUR/GBP fall back to `fallback`
 * (catalogs are already home-denominated; we do not invent FX).
 */
export function jetATableAmount(icao: string, home: CurrencyCode, fallback = JET_A_DEFAULT_CAD): number {
  const code = icaoKey(icao);
  const icaoQuote = code ? ICAO_QUOTES[code] : undefined;
  if (icaoQuote) return amountFromPair(icaoQuote, home, fallback);
  const region = regionRow(jetARegionIdForIcao(code));
  if (region.id !== "OTHER") return amountFromPair(region, home, fallback);
  const country = regionRow(jetACountryIdForIcao(code));
  if (country.id !== "OTHER") return amountFromPair(country, home, fallback);
  return amountFromPair(REGION_BY_ID.OTHER, home, fallback);
}

/**
 * Jet A ballpark for one ICAO.
 * Order: Settings override → catalog ICAO row → curated ICAO → region → country → `*` / fallback.
 * Avgas is out of scope. Invalid ICAO fails soft to the company default.
 */
export function lookupJetA(input: LookupJetAInput = {}): JetAQuote {
  const home = homeCurrencyOf(input.homeCurrency ?? input.catalogs?.currency);
  const fallback = Number.isFinite(input.fallbackPerGal)
    ? roundGal(input.fallbackPerGal as number)
    : catalogDefaultJetA(input.catalogs) ?? (home === "USD" ? JET_A_DEFAULT_USD : JET_A_DEFAULT_CAD);
  const code = icaoKey(input.icao);
  const regionId = jetARegionIdForIcao(code);
  const countryId = jetACountryIdForIcao(code);

  if (!code) {
    return quote("", fallback, home, "default", "OTHER", "No ICAO — company Jet A default. Not Avgas.");
  }

  const override = jetAOverrideFor(input.overrides, code);
  if (override) {
    return quote(code, override.pricePerUsGal, home, "override", regionId, JET_A_OVERRIDE_NOTE);
  }

  const catalogExact = catalogJetA(input.catalogs, code);
  if (catalogExact != null) {
    return quote(code, catalogExact, home, "catalog", regionId, "Money catalog Jet-A row");
  }

  const icaoQuote = ICAO_QUOTES[code];
  if (icaoQuote) {
    return quote(
      code,
      amountFromPair(icaoQuote, home, fallback),
      home,
      "icao",
      regionId,
      icaoQuote.note,
    );
  }

  if (regionId !== countryId && regionId !== "OTHER") {
    const region = regionRow(regionId);
    return quote(
      code,
      amountFromPair(region, home, fallback),
      home,
      "region",
      regionId,
      `${region.label} Jet A default`,
    );
  }

  if (countryId !== "OTHER") {
    const country = regionRow(countryId);
    return quote(
      code,
      amountFromPair(country, home, fallback),
      home,
      "country",
      countryId,
      `${country.label} Jet A default`,
    );
  }

  return quote(code, fallback, home, "default", "OTHER", "Company Jet A default. Not a live FBO API.");
}

export function formatJetAPrice(quote: Pick<JetAQuote, "pricePerUsGal" | "currency">): string {
  const abs = Math.abs(quote.pricePerUsGal).toLocaleString(quote.currency === "USD" ? "en-US" : "en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `$${abs} ${quote.currency} / US gal`;
}

export function formatJetASource(quote: Pick<JetAQuote, "source" | "regionLabel">): string {
  if (quote.source === "region") return `${quote.regionLabel} default`;
  if (quote.source === "country") return `${quote.regionLabel} default`;
  return JET_A_SOURCE_LABEL[quote.source];
}

export function formatJetALine(q: JetAQuote): string {
  return `${formatJetAPrice(q)} · ${formatJetASource(q)}`;
}

/** Destinations + home / extra bases for the Settings override table. */
export function jetAIcaosForDesk(input: {
  scenery?: readonly { icao?: string }[];
  homeIcao?: string;
  extraBases?: readonly string[];
}): string[] {
  const set = new Set<string>();
  const add = (value?: string) => {
    const code = icaoKey(value);
    if (code && code.length >= 3 && code.length <= 4) set.add(code);
  };
  add(input.homeIcao);
  for (const base of input.extraBases ?? []) add(base);
  for (const row of input.scenery ?? []) add(row.icao);
  return [...set].sort();
}
